import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

// Types
export interface GeofenceAction {
  notify: boolean;
  rapidRefresh: {
    enabled: boolean;
    intervalSeconds: number;
    durationMinutes: number;
  };
}

export interface Geofence {
  id: string;
  name: string;
  ne: { lat: number; lng: number };
  sw: { lat: number; lng: number };
  actions: {
    arrival: GeofenceAction;
    departure: GeofenceAction;
  };
  createdAt: number;
  updatedAt: number;
}

export interface GeofenceInput {
  name: string;
  ne: { lat: number; lng: number };
  sw: { lat: number; lng: number };
  actions: {
    arrival: GeofenceAction;
    departure: GeofenceAction;
  };
}

class GeofenceService {
  private apiUrl: string;

  constructor() {
    this.apiUrl = process.env.REACT_APP_API_URL || '';
    if (!this.apiUrl) {
      console.warn('API URL is not defined. Set REACT_APP_API_URL environment variable.');
    }
  }

  private async getAuthToken(): Promise<string> {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens?.idToken) {
        throw new Error('No ID token available');
      }
      return session.tokens.idToken.toString();
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw error;
    }
  }

  private getBaseUrl(): string {
    return this.apiUrl.endsWith('/')
      ? this.apiUrl.slice(0, -1)
      : this.apiUrl;
  }

  // Get all geofences
  async getAllGeofences(): Promise<Geofence[]> {
    if (!this.apiUrl) {
      throw new Error('API URL is not defined. Set REACT_APP_API_URL environment variable.');
    }

    try {
      const baseUrl = this.getBaseUrl();
      const token = await this.getAuthToken();
      const response = await axios.get(`${baseUrl}/geofences`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching geofences:', error);
      throw error;
    }
  }

  // Get a specific geofence by ID
  async getGeofenceById(id: string): Promise<Geofence> {
    if (!this.apiUrl) {
      throw new Error('API URL is not defined. Set REACT_APP_API_URL environment variable.');
    }

    try {
      const baseUrl = this.getBaseUrl();
      const token = await this.getAuthToken();
      const response = await axios.get(`${baseUrl}/geofences/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error(`Error fetching geofence with ID ${id}:`, error);
      throw error;
    }
  }

  // Create a new geofence
  async createGeofence(data: GeofenceInput): Promise<Geofence> {
    if (!this.apiUrl) {
      throw new Error('API URL is not defined. Set REACT_APP_API_URL environment variable.');
    }

    try {
      const baseUrl = this.getBaseUrl();
      const token = await this.getAuthToken();
      const response = await axios.post(`${baseUrl}/geofences`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error creating geofence:', error);
      throw error;
    }
  }

  // Update an existing geofence
  async updateGeofence(id: string, data: GeofenceInput): Promise<Geofence> {
    if (!this.apiUrl) {
      throw new Error('API URL is not defined. Set REACT_APP_API_URL environment variable.');
    }

    try {
      const baseUrl = this.getBaseUrl();
      const token = await this.getAuthToken();
      const response = await axios.put(`${baseUrl}/geofences/${id}`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error(`Error updating geofence with ID ${id}:`, error);
      throw error;
    }
  }

  // Delete a geofence
  async deleteGeofence(id: string): Promise<void> {
    if (!this.apiUrl) {
      throw new Error('API URL is not defined. Set REACT_APP_API_URL environment variable.');
    }

    try {
      const baseUrl = this.getBaseUrl();
      const token = await this.getAuthToken();
      await axios.delete(`${baseUrl}/geofences/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error(`Error deleting geofence with ID ${id}:`, error);
      throw error;
    }
  }
}

// Create a singleton instance
const geofenceService = new GeofenceService();
export default geofenceService;
